<template>
  <Modal title="Status history" :max-width="560" @close="$emit('close')">
    <template v-if="member" #header>
      <div class="historySubject">
        <span class="historySubjectLabel">Status history</span>
        <CandidateRowIdentity :member="member" show-contact-account-icon />
      </div>
    </template>

    <div v-if="loading" class="historyLoading">Loading…</div>
    <div v-else-if="error" class="historyError">{{ error }}</div>
    <div v-else-if="transitions.length === 0" class="historyEmpty">No status changes recorded yet.</div>
    <div v-else class="historyList">
      <div v-for="t in transitions" :key="t.id" class="historyRow">
        <div class="historyChange">
          <span class="historyKindTag" :class="`historyKindTag--${eventKind(t).color}`">
            <AppIcon :name="eventKind(t).icon" :size="11" />
            {{ eventKind(t).label }}
          </span>
          <span class="historyState">{{ stateLabel(t.from_state, t.event_type) }}</span>
          <AppIcon name="arrow_right" :size="12" class="historyArrow" />
          <span class="historyState historyStateTo">{{ stateLabel(t.to_state, t.event_type) }}</span>
        </div>
        <div class="historyMeta">
          <span class="historyTime" :title="absoluteTime(t.created_at)">{{ relativeTime(t.created_at) }}</span>
          <span v-if="t.reason" class="historyReason">{{ reasonLabel(t.reason) }}</span>
          <span v-if="actorLabel(t)" class="historyActor">{{ actorLabel(t) }}</span>
          <router-link v-if="t.agent_run_id" class="historyRunLink" :to="`/agent-runs/${t.agent_run_id}`">View run →</router-link>
        </div>
        <p v-if="t.reason_detail" class="historyDetail">{{ t.reason_detail }}</p>
      </div>
    </div>
    <template #footer>
      <button class="btn secondary" @click="$emit('close')">Close</button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import Modal from './Modal.vue'
import AppIcon from '../../components/AppIcon.vue'
import CandidateRowIdentity from './CandidateRowIdentity.vue'
import { apiFetch } from '../../services/api'
import { absoluteTime, reasonLabel, relativeTime } from '../utils/candidates'

interface HistorySubject {
  kind: 'account' | 'contact'
  account_domain?: string | null
  account_name?: string | null
  contact_name?: string | null
  contact_email?: string | null
  contact_title?: string | null
}

interface Transition {
  id: string
  event_type: string
  from_state: string
  to_state: string
  reason: string | null
  reason_detail: string | null
  actor_user_id: string | null
  actor_user_name: string | null
  actor_user_email: string | null
  agent_run_id: string | null
  created_at: string
}

const props = defineProps<{
  membershipId: string
  member?: HistorySubject | null
}>()

defineEmits<{ close: [] }>()

const loading = ref(true)
const error = ref('')
const transitions = ref<Transition[]>([])

const CADENCE_LABELS: Record<string, string> = {
  active: 'Active cadence',
  opportunistic: 'Opportunistic cadence',
  timed_followup: 'Timed follow-up',
  off: 'Cadence off',
}

function stateLabel(state: string, eventType: string): string {
  if (eventType === 'prospecting_cadence') return CADENCE_LABELS[state] || state
  return state.replace(/_/g, ' ')
}

interface EventKind { label: string; icon: string; color: string }

// Mirrors the account-activity timeline's classification (timelineFormat.ts)
// so the same event reads with the same icon/label everywhere it shows up.
function eventKind(t: Transition): EventKind {
  if (t.event_type === 'prospecting_cadence') return { label: 'Cadence', icon: 'wait', color: 'accent' }
  if (t.event_type === 'email_swapped') return { label: 'Email', icon: 'email', color: 'neutral' }
  if (t.event_type === 'sequence_enroll' || t.event_type === 'sequence_step' || t.event_type === 'sequence_exit') {
    return { label: 'Sequence', icon: 'sequence', color: 'neutral' }
  }
  if (t.event_type === 'action') {
    const kind = t.reason ? t.reason.charAt(0).toUpperCase() + t.reason.slice(1) : ''
    return { label: kind ? `${kind} action` : 'Action', icon: 'action', color: 'neutral' }
  }
  return { label: 'Candidate', icon: 'sales_play', color: 'neutral' }
}

function actorLabel(t: Transition): string {
  if (t.actor_user_name) return t.actor_user_name
  if (t.actor_user_email) return t.actor_user_email
  return ''
}

onMounted(async () => {
  try {
    transitions.value = await apiFetch(`/sales-plays/memberships/${props.membershipId}/transitions`)
  } catch (e: any) {
    error.value = e?.message || 'Failed to load history'
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.historySubject {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.historySubjectLabel {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide);
  color: var(--color-text-tertiary);
}

.historyLoading, .historyError, .historyEmpty {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-tertiary);
  padding: var(--space-4) 0;
}
.historyError { color: var(--color-danger, #b91c1c); }

.historyList {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.historyRow {
  padding-bottom: var(--space-3);
  border-bottom: 1px solid var(--color-border-subtle);
}
.historyRow:last-child { border-bottom: none; padding-bottom: 0; }

.historyChange {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
}
.historyKindTag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide);
  color: var(--color-text-tertiary);
  padding: 1px var(--space-2);
  border-radius: var(--radius-full);
  background: var(--color-bg-subtle);
  flex-shrink: 0;
}
.historyKindTag--accent {
  color: var(--color-accent);
  background: var(--color-accent-soft);
}
.historyState { text-transform: capitalize; }
.historyStateTo { color: var(--color-accent); }
.historyArrow { color: var(--color-text-tertiary); flex-shrink: 0; }

.historyMeta {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-top: 2px;
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
}
.historyReason::before { content: '·'; margin-right: var(--space-2); }
.historyActor::before { content: '·'; margin-right: var(--space-2); color: var(--color-text-tertiary); }
.historyRunLink {
  color: var(--color-accent);
  text-decoration: none;
  font-weight: var(--fw-semibold);
}
.historyRunLink::before { content: '·'; margin-right: var(--space-2); color: var(--color-text-tertiary); }
.historyRunLink:hover { text-decoration: underline; }

.historyDetail {
  margin: var(--space-1) 0 0;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
  line-height: var(--lh-normal);
}
</style>
